import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { FileText, Bookmark, CalendarClock, Sparkles, ChevronRight, MapPin } from "lucide-react";

export default function CandidateDashboard() {
  const [apps, setApps] = useState([]);
  const [saved, setSaved] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [recs, setRecs] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/candidates/me").then(r => setProfile(r.data));
    api.get("/applications").then(r => setApps(r.data.items || []));
    api.get("/jobs/saved/list").then(r => setSaved(r.data.items || []));
    api.get("/interviews").then(r => setInterviews(r.data.items || []));
    api.post("/ai/recommend-jobs").then(r => setRecs(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid={TID.candidateDashboard}>
      <div className="mb-8">
        <div className="eyebrow mb-2">Candidate Portal</div>
        <h1 className="font-display text-3xl font-bold text-navy">Welcome{profile?.user?.name ? `, ${profile.user.name.split(" ")[0]}` : ""}.</h1>
        <p className="text-slate-500 text-sm mt-1">Your applications, interviews, and personalized jobs.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: FileText, label: "Applications", val: apps.length, to: "/dashboard/candidate/applications" },
          { icon: Bookmark, label: "Saved jobs", val: saved.length, to: "/dashboard/candidate/saved" },
          { icon: CalendarClock, label: "Upcoming interviews", val: interviews.filter(i => i.status === "Scheduled").length, to: "/dashboard/candidate/interviews" },
          { icon: Sparkles, label: "Recommended", val: recs.length, to: "/jobs" },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <Link to={c.to} key={i} className="card-lift border border-slate-200 rounded-xl p-5 bg-white block">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{c.label}</div>
                  <div className="mt-2 font-display text-3xl font-bold text-navy">{c.val}</div>
                </div>
                <div className="h-10 w-10 rounded-lg bg-gold/10 text-gold grid place-items-center"><Icon className="w-5 h-5" /></div>
              </div>
            </Link>
          );
        })}
      </div>

      {profile && !profile.resume_path && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-center justify-between">
          <div>Add a resume to boost your match with recruiters.</div>
          <Link to="/dashboard/candidate/profile" className="font-medium text-amber-900 hover:text-amber-700 inline-flex items-center gap-1">Complete profile <ChevronRight className="w-4 h-4" /></Link>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="border border-slate-200 rounded-xl bg-white p-6">
          <div className="eyebrow mb-3">Recommended for you</div>
          <div className="space-y-3">
            {recs.slice(0, 5).map(j => (
              <Link key={j.id} to={`/jobs/${j.id}`} className="block p-3 rounded-lg border border-slate-100 hover:border-navy transition-colors">
                <div className="text-sm font-medium text-navy">{j.title}</div>
                <div className="text-xs text-slate-500">{j.employer_name} • {j.city}{j.state ? `, ${j.state}` : ""}</div>
              </Link>
            ))}
            {recs.length === 0 && <div className="text-sm text-slate-500">Complete your profile to get personalized job matches.</div>}
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl bg-white p-6">
          <div className="eyebrow mb-3">Recent applications</div>
          <div className="divide-y">
            {apps.slice(0, 5).map(a => (
              <div key={a.id} className="py-3 text-sm flex justify-between items-center">
                <div>
                  <div className="font-medium text-navy">{a.job_title}</div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.employer_name}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-slate-100">{a.status}</span>
              </div>
            ))}
            {apps.length === 0 && <div className="text-sm text-slate-500 py-4">You haven't applied to jobs yet. <Link to="/jobs" className="text-navy font-medium">Browse jobs →</Link></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
